import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { CircleNotch } from '@phosphor-icons/react';
import type { GeocodeSuggestion } from '@pms/shared';
import type { LocationStatus } from '../../hooks/useLocationAutocomplete';

export interface LocationOption {
  id: string;
  label: string;
  sublabel?: string;
  /** Present for geo "Places" rows; absent for own "BestAcco stays" rows. */
  suggestion?: GeocodeSuggestion;
}

export interface LocationGroup {
  label: string;
  items: LocationOption[];
}

interface Props {
  value: string;
  onChange: (text: string) => void;
  onSelect: (opt: LocationOption) => void;
  groups: LocationGroup[];
  status: LocationStatus;
  placeholder?: string;
  ariaLabel?: string;
  inputClassName?: string;
}

/**
 * Accessible location combobox (WAI-ARIA 1.2) for the storefront "Where to"
 * field (TASK-146). Renders grouped suggestions with roving arrow-key nav; the
 * host owns the query + sources (augmented: own stays first, then geo places).
 * Duplicated per app (see apps/web) — only the ARIA contract is shared.
 */
export function LocationSearchInput({
  value,
  onChange,
  onSelect,
  groups,
  status,
  placeholder,
  ariaLabel,
  inputClassName = '',
}: Props) {
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const flat = groups.flatMap((g) => g.items);
  const hasRows = flat.length > 0;
  const showDropdown =
    focused && value.trim().length >= 2 && (status !== 'idle' || hasRows);

  // Reset roving highlight whenever the option set changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [value, status]);

  // Outside-click closes (mirrors the guestsRef pattern in SearchBar).
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    }
    if (showDropdown) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showDropdown]);

  const optionId = (index: number) => `${listboxId}-opt-${index}`;

  function commit(index: number) {
    const opt = flat[index];
    if (!opt) return;
    onSelect(opt);
    setActiveIndex(-1);
    setFocused(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      if (!hasRows) return;
      e.preventDefault();
      setFocused(true);
      setActiveIndex((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      if (!hasRows) return;
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? flat.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      // Only intercept when a row is highlighted, so the form can still submit.
      if (showDropdown && activeIndex >= 0) {
        e.preventDefault();
        commit(activeIndex);
      }
    } else if (e.key === 'Escape') {
      if (showDropdown) {
        e.preventDefault();
        setFocused(false);
        setActiveIndex(-1);
      }
    }
  }

  let flatIndex = -1;

  return (
    <div ref={rootRef} className="relative w-full">
      <input
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex >= 0 ? optionId(activeIndex) : undefined
        }
        aria-busy={status === 'loading'}
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={inputClassName}
        autoComplete="off"
      />

      {status === 'loading' && (
        <CircleNotch
          className="pointer-events-none absolute right-1 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-clay"
          aria-hidden="true"
        />
      )}

      {showDropdown && (
        <ul
          role="listbox"
          id={listboxId}
          className="absolute left-0 top-full z-30 mt-3 max-h-80 w-[min(28rem,calc(100vw-3rem))] overflow-auto rounded-2xl border border-sand bg-white p-1.5 shadow-[0_24px_60px_-30px_rgba(28,26,23,0.6)]"
        >
          {!hasRows && status === 'empty' && (
            <li className="px-3 py-2 text-sm text-ink-muted" aria-disabled="true">
              No matches for &ldquo;{value.trim()}&rdquo;
            </li>
          )}
          {!hasRows && status === 'error' && (
            <li className="px-3 py-2 text-sm text-ink-muted" aria-disabled="true">
              Couldn&rsquo;t load suggestions — keep typing
            </li>
          )}
          {hasRows &&
            groups.map((group) =>
              group.items.length === 0 ? null : (
                <li key={group.label} role="group" aria-label={group.label}>
                  <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {group.label}
                  </div>
                  <ul className="mb-1">
                    {group.items.map((opt) => {
                      flatIndex += 1;
                      const idx = flatIndex;
                      const active = idx === activeIndex;
                      return (
                        <li
                          key={opt.id}
                          id={optionId(idx)}
                          role="option"
                          aria-selected={active}
                          onMouseEnter={() => setActiveIndex(idx)}
                          onMouseDown={(e) => {
                            // Prevent input blur before the click commits.
                            e.preventDefault();
                            commit(idx);
                          }}
                          className={`cursor-pointer rounded-xl px-3 py-2 text-sm ${
                            active ? 'bg-cream text-ink' : 'text-ink'
                          }`}
                        >
                          <span className="block truncate">{opt.label}</span>
                          {opt.sublabel && (
                            <span className="block truncate text-xs text-ink-muted">
                              {opt.sublabel}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ),
            )}
        </ul>
      )}
    </div>
  );
}
